import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getSession, getAgent, clearSession, setSession, getAgentByKey, validateSession, AGENT_KEY_RE } from './agentAPI';

const RATE_LIMIT_MAX   = 5;
const RATE_LIMIT_MS    = 60_000;

const AgentContext = createContext(null);

export function AgentProvider({ children }) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginAttempts = useRef([]);

  useEffect(() => {
    (async () => {
      const sess = getSession();
      if (sess) {
        const valid = validateSession(sess);
        if (valid) {
          try {
            const a = await getAgent(sess.agentId);
            if (a) setAgent(a);
            else clearSession();
          } catch { clearSession(); }
        } else {
          clearSession();
        }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (agentKey) => {
    if (!AGENT_KEY_RE.test(agentKey)) return { ok: false, locked: false };
    const now = Date.now();
    loginAttempts.current = loginAttempts.current.filter(t => now - t < RATE_LIMIT_MS);
    if (loginAttempts.current.length >= RATE_LIMIT_MAX) {
      const wait = Math.ceil((RATE_LIMIT_MS - (now - loginAttempts.current[0])) / 1000);
      return { ok: false, locked: true, wait };
    }
    loginAttempts.current.push(now);
    try {
      const a = await getAgentByKey(agentKey);
      if (!a) return { ok: false, locked: false };
      loginAttempts.current = [];
      setSession(a.agentId, a.agentKey);
      setAgent(a);
      return { ok: true };
    } catch { return { ok: false, locked: false }; }
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setAgent(null);
  }, []);

  const refreshAgent = useCallback(async () => {
    const sess = getSession();
    if (sess) {
      const valid = validateSession(sess);
      if (valid) {
        try {
          const a = await getAgent(sess.agentId);
          if (a) setAgent(a);
        } catch { /* ignore */ }
      }
    }
  }, []);

  return (
    <AgentContext.Provider value={{ agent, loading, login, logout, refreshAgent }}>
      {children}
    </AgentContext.Provider>
  );
}

export const useAgent = () => useContext(AgentContext);
